'use client';

import { useEffect } from 'react';
import { Package, CheckCircle, XCircle, ArrowLeftRight, Wallet, AlertTriangle, Ban, Headphones, Sparkles } from 'lucide-react';
import policyData from '../../parsed-policies.json';
import { SitePage, PageHero, Section, Card, IconBadge, Mark, PolicyText } from '@/components/site/page-kit';

const returnsData = policyData.returns;
const sectionIcons = [Package, CheckCircle, XCircle, ArrowLeftRight, Wallet, AlertTriangle, Ban, Headphones];

/** Returns-only paragraph types (numbered steps, "Important:" and "Pro Tip:" callouts); everything else goes to PolicyText. */
function ReturnsParagraph({ para }: { para: string }) {
  if (para.match(/^\d+\./)) {
    return (
      <ol className="space-y-3 text-[15px] leading-[1.8] text-hv-soft">
        {para.split('\n').filter((line) => line.trim()).map((item, j) => {
          const num = item.match(/^\d+/)?.[0];
          return (
            <li key={j} className="flex items-start gap-4">
              <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-hv-card font-hv-mono text-[12px] text-hv-orange">
                {num}
              </span>
              <span>{item.replace(/^\d+\.\s*/, '')}</span>
            </li>
          );
        })}
      </ol>
    );
  }
  if (para.startsWith('Important:')) {
    return (
      <div className="flex gap-3 rounded-xl border border-hv-orange/30 bg-hv-orange/[0.06] px-5 py-4">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-hv-orange" strokeWidth={1.8} />
        <p className="text-[14px] leading-relaxed text-hv-ink">{para}</p>
      </div>
    );
  }
  if (para.startsWith('Pro Tip:')) {
    return (
      <div className="flex gap-3 rounded-xl border border-hv-line bg-hv-card px-5 py-4">
        <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-hv-orange" strokeWidth={1.8} />
        <p className="text-[14px] leading-relaxed text-hv-ink">{para}</p>
      </div>
    );
  }
  return <PolicyText text={para} />;
}

export default function ReturnsPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  return (
    <SitePage>
      <PageHero
        eyebrow="Homenest Retail Tech India Pvt Ltd"
        title={<>Return <Mark>Policy</Mark></>}
        description="Our transparent and customer-friendly return & refund guidelines"
        meta="Last updated: February 6, 2026"
      />

      <Section>
        <div className="grid max-w-[960px] gap-4 md:gap-5">
          {returnsData.sections.map((section, index) => {
            const Icon = sectionIcons[index % sectionIcons.length];
            return (
              <Card as="section" key={index}>
                <div className="mb-5 flex items-center gap-4">
                  <IconBadge icon={Icon} />
                  <div className="min-w-0">
                    <div className="font-hv-mono text-[12px] text-hv-orange">{String(index + 1).padStart(2, '0')}</div>
                    <h2 className="text-lg tracking-[-0.01em] md:text-xl">{section.title}</h2>
                  </div>
                </div>
                <div className="space-y-4 md:pl-[60px]">
                  {section.content.split('\n\n').map((para, i) => (
                    <ReturnsParagraph key={i} para={para} />
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      </Section>
    </SitePage>
  );
}
